import { get, post, put, del } from "@/shared/utils/fetchApi";

/**
 * Interface para representar um usuário de produto
 */
export interface WorkspaceUser {
  id: string;
  product_id: string;
  user_id: string;
  created_at: string;
  created_by: string;
  role: string;
}

/**
 * Interface para a criação de uma atribuição de usuário a produto.
 * `created_by` não entra nos DTOs de requisição: o backend o deriva do token.
 */
export interface CreateWorkspaceUserDTO {
  user_id: string;
  role?: string;
}

/**
 * Interface para a atribuição em lote de usuários a um produto
 */
export interface AssignUsersDTO {
  user_ids: string[];
}

/**
 * Interface para atualização em lote dos produtos de um usuário
 */
export interface UpdateUserWorkspacesDTO {
  product_ids: string[];
}

/**
 * Interface para atualização em lote dos usuários de um produto
 */
export interface UpdateWorkspaceUsersDTO {
  user_ids: string[];
}

/**
 * Classe de serviço para encapsular todas as operações relacionadas às atribuições de usuários a produtos
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class WorkspaceUsersService {

  /**
   * Obtém todos os usuários atribuídos a um produto
   * @param workspaceId ID do produto
   * @returns Lista completa de usuários do produto
   */
  static async getWorkspaceUsers(
    workspaceId: string,
  ): Promise<WorkspaceUser[]> {
    try {
      // v2 reestrutura URL: /products/:productId/users (nested REST).
      return await get<WorkspaceUser[]>(`/products/${workspaceId}/users`);
    } catch (error) {
      console.error(
        `Erro ao buscar usuários do produto com ID ${workspaceId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Obtém apenas os IDs dos usuários atribuídos a um produto
   * @param workspaceId ID do produto
   * @returns Lista de IDs de usuários
   */
  static async getWorkspaceUserIds(workspaceId: string): Promise<string[]> {
    try {
      return await get<string[]>(`/products/${workspaceId}/users/ids`);
    } catch (error) {
      // Retornar array vazio em vez de propagar o erro
      // Isso evita que a UI quebre em caso de falha na API
      return [];
    }
  }

  /**
   * Atribui um único usuário a um produto
   * @param workspaceId ID do produto
   * @param data Dados do usuário a ser atribuído
   * @returns Dados da atribuição criada
   */
  static async assignUser(
    workspaceId: string,
    data: CreateWorkspaceUserDTO,
  ): Promise<WorkspaceUser> {
    try {
      // `created_by` vem do JWT: o body leva só user_id/role.
      return await post<WorkspaceUser>(`/products/${workspaceId}/users`, {
        user_id: data.user_id,
        role: data.role,
      });
    } catch (error) {
      console.error(
        `Erro ao atribuir usuário ao produto com ID ${workspaceId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Atribui múltiplos usuários a um produto
   * @param workspaceId ID do produto
   * @param data Dados dos usuários a serem atribuídos
   */
  static async assignUsers(
    workspaceId: string,
    data: AssignUsersDTO,
  ): Promise<void> {
    try {
      await post(`/products/${workspaceId}/users/batch`, {
        user_ids: data.user_ids,
      });
      return;
    } catch (error) {
      console.error(
        `Erro ao atribuir usuários ao produto com ID ${workspaceId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Remove a atribuição de um usuário específico de um produto
   * @param workspaceId ID do produto
   * @param userId ID do usuário
   */
  static async unassignUser(
    workspaceId: string,
    userId: string,
  ): Promise<void> {
    try {
      await del(`/products/${workspaceId}/users/${userId}`);
      return;
    } catch (error) {
      console.error(
        `Erro ao remover atribuição de usuário ${userId} do produto ${workspaceId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Remove todas as atribuições de usuários de um produto
   * @param workspaceId ID do produto
   */
  static async unassignAllUsers(workspaceId: string): Promise<void> {
    try {
      // v2 exige header X-Confirm-Wipe: yes pra prevenir wipe acidental.
      await del(`/products/${workspaceId}/users`, {
        headers: { "X-Confirm-Wipe": "yes" },
      });
      return;
    } catch (error) {
      console.error(
        `Erro ao remover atribuições de usuários do produto com ID ${workspaceId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Atualiza todos os produtos vinculados a um usuário específico
   * @param userId ID do usuário
   * @param data Dados com os IDs dos produtos a serem vinculados
   * @returns Estatísticas sobre as alterações realizadas
   */
  static async updateUserProducts(
    userId: string,
    data: UpdateUserWorkspacesDTO,
  ): Promise<{ added: number; removed: number; total: number }> {
    try {
      return await put<{ added: number; removed: number; total: number }>(
        `/users/${userId}/products`,
        { product_ids: data.product_ids },
      );
    } catch (error) {
      console.error(`Erro ao atualizar produtos do usuário ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Atualiza todos os usuários vinculados a um produto específico de forma atômica
   * @param workspaceId ID do produto
   * @param data Dados com os IDs dos usuários a serem vinculados
   * @returns Estatísticas sobre as alterações realizadas
   */
  static async updateProductUsers(
    workspaceId: string,
    data: UpdateWorkspaceUsersDTO,
  ): Promise<{ added: number; removed: number; total: number }> {
    return await put<{ added: number; removed: number; total: number }>(
      `/products/${workspaceId}/users`,
      { user_ids: data.user_ids },
    );
  }
}
